const shelfInstances = new WeakMap();

class RyoxzelUgcShelf {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-ryoxzel-ugc-viewport]');
    this.cards = Array.from(section.querySelectorAll('[data-ryoxzel-ugc-card]'));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.frame = 0;
    this.lightbox = document.querySelector('[data-ryoxzel-ugc-lightbox]');
    this.lightboxDialog = this.lightbox?.querySelector('dialog');
    this.lightboxViewport = this.lightbox?.querySelector('[data-ryoxzel-ugc-lightbox-viewport]');
    this.lightboxSlides = this.lightbox ? Array.from(this.lightbox.querySelectorAll('[data-ryoxzel-ugc-lightbox-slide]')) : [];
    this.lightboxIndex = 0;
    this.lightboxMountedIndex = null;
    this.lightboxFrame = 0;
    this.lightboxTrigger = null;
    this.lightboxMuted = true;
    this.progressFrame = 0;
    this.progressVideo = null;
    this.progressFill = null;
    this.progressAbortController = null;

    if (!this.viewport || this.cards.length === 0) return;

    const { signal } = this.abortController;
    this.cards.forEach((card, index) => card.querySelector('[data-ryoxzel-ugc-trigger]')?.addEventListener('click', () => this.openLightbox(index, card), { signal }));
    this.lightboxDialog?.addEventListener('close', () => this.closeLightboxMedia(), { signal });
    this.lightbox?.addEventListener('dialog:close', () => this.lightboxTrigger?.focus(), { signal });
    this.lightboxViewport?.addEventListener('scroll', () => this.scheduleLightboxActive(), { passive: true, signal });
    this.lightbox?.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-ryoxzel-ugc-lightbox-sound]');
      if (button) this.toggleLightboxSound(button);
    }, { signal });
    this.viewport.addEventListener('scroll', () => this.scheduleActiveCard(), { passive: true, signal });
    window.addEventListener('resize', () => this.scheduleActiveCard(), { passive: true, signal });
    this.scheduleActiveCard();
  }

  scheduleActiveCard() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateActiveCard());
  }

  updateActiveCard() {
    if (!this.viewport || this.cards.length === 0) return;

    const viewportRect = this.viewport.getBoundingClientRect();
    const viewportCenter = viewportRect.left + viewportRect.width / 2;
    let activeCard = this.cards[0];
    let closestDistance = Number.POSITIVE_INFINITY;

    this.cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeCard = card;
      }
    });

    this.cards.forEach((card) => {
      const isActive = card === activeCard;
      card.classList.toggle('is-active', isActive);
      card.querySelector('[data-ryoxzel-ugc-trigger]')?.toggleAttribute('aria-current', isActive);
    });
  }

  openLightbox(index, trigger) {
    if (!this.lightbox || this.lightboxSlides.length === 0) return;
    this.lightboxTrigger = trigger;
    this.lightboxMuted = true;
    this.updateAllSoundButtons();
    this.lightbox.showDialog();
    requestAnimationFrame(() => requestAnimationFrame(() => this.scrollToLightboxSlide(index, 'auto')));
  }

  scrollToLightboxSlide(index, behavior = this.reducedMotion.matches ? 'auto' : 'smooth') {
    const total = this.lightboxSlides.length;
    if (!total) return;
    const normalizedIndex = (index + total) % total;
    this.activateLightboxSlide(normalizedIndex);
    this.lightboxSlides[normalizedIndex]?.scrollIntoView({ behavior, inline: 'center', block: 'nearest' });
  }

  scheduleLightboxActive() {
    cancelAnimationFrame(this.lightboxFrame);
    this.lightboxFrame = requestAnimationFrame(() => {
      if (!this.lightboxViewport) return;
      const viewportRect = this.lightboxViewport.getBoundingClientRect();
      const center = viewportRect.left + viewportRect.width / 2;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      this.lightboxSlides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < nearestDistance) { nearestDistance = distance; nearestIndex = index; }
      });
      this.activateLightboxSlide(nearestIndex);
    });
  }

  activateLightboxSlide(index) {
    const total = this.lightboxSlides.length;
    if (!total || !this.lightbox) return;
    this.lightboxIndex = (index + total) % total;
    const slide = this.lightboxSlides[this.lightboxIndex];
    this.lightboxSlides.forEach((item, itemIndex) => item.classList.toggle('active', itemIndex === this.lightboxIndex));
    const status = this.lightbox.querySelector('[data-ryoxzel-ugc-lightbox-status]');
    if (status) {
      const title = slide.dataset.title;
      status.textContent = title
        ? `Video ${this.lightboxIndex + 1} of ${total} · ${title}`
        : `Video ${this.lightboxIndex + 1} of ${total}`;
    }
    const cta = this.lightbox.querySelector('[data-ryoxzel-ugc-lightbox-cta]');
    if (cta) cta.href = slide.dataset.url || cta.href;
    const ctaLabel = this.lightbox.querySelector('[data-ryoxzel-ugc-lightbox-cta-label]');
    if (ctaLabel) ctaLabel.textContent = slide.dataset.ctaLabel || 'Shop squishies';
    if (this.lightboxMountedIndex === this.lightboxIndex) return;
    this.closeLightboxMedia();
    const mediaContainer = slide.querySelector('[data-ryoxzel-ugc-lightbox-media]');
    const source = slide.querySelector('[data-ryoxzel-ugc-lightbox-media-source]');
    if (mediaContainer && source?.content.childElementCount) mediaContainer.replaceChildren(source.content.cloneNode(true));
    const media = mediaContainer?.querySelector('deferred-media');
    media?.loadContent?.(false); media?.playMedia?.();
    const video = mediaContainer?.querySelector('video');
    if (video) { video.muted = this.lightboxMuted; video.playsInline = true; this.startVideoProgress(video, slide); const playback = video.play(); playback?.catch(() => media?.pauseMedia?.()); }
    this.updateSoundButton(slide);
    this.lightboxMountedIndex = this.lightboxIndex;
  }

  toggleLightboxSound(button) {
    const slide = button.closest('[data-ryoxzel-ugc-lightbox-slide]');
    if (!slide?.classList.contains('active')) return;
    const video = slide.querySelector('video');
    if (!video) return;
    this.lightboxMuted = !this.lightboxMuted;
    video.muted = this.lightboxMuted;
    this.updateSoundButton(slide);
  }

  updateSoundButton(slide) {
    const button = slide?.querySelector('[data-ryoxzel-ugc-lightbox-sound]');
    if (!button) return;
    const soundOn = !this.lightboxMuted;
    button.setAttribute('aria-pressed', String(soundOn));
    button.setAttribute('aria-label', soundOn ? 'Mute video' : 'Turn sound on');
  }

  updateAllSoundButtons() { this.lightboxSlides.forEach((slide) => this.updateSoundButton(slide)); }

  startVideoProgress(video, slide) {
    this.stopVideoProgress(true);
    const fill = slide.querySelector('[data-ryoxzel-ugc-lightbox-progress-fill]');
    if (!fill) return;
    this.progressVideo = video;
    this.progressFill = fill;
    this.progressAbortController = new AbortController();
    const { signal } = this.progressAbortController;
    const sync = () => this.updateVideoProgress();
    const start = () => { this.stopProgressFrame(); this.updateVideoProgress(); this.progressFrame = requestAnimationFrame(() => this.runProgressFrame()); };
    const stop = () => { this.stopProgressFrame(); this.updateVideoProgress(); };
    ['loadedmetadata', 'durationchange', 'timeupdate', 'seeking', 'seeked'].forEach((eventName) => video.addEventListener(eventName, sync, { signal }));
    video.addEventListener('play', start, { signal });
    video.addEventListener('pause', stop, { signal });
    video.addEventListener('ended', () => { this.stopProgressFrame(); this.setProgress(1); }, { signal });
    this.updateVideoProgress();
    if (!video.paused && !video.ended) start();
  }

  runProgressFrame() {
    if (!this.progressVideo || this.progressVideo.paused || this.progressVideo.ended) { this.stopProgressFrame(); return; }
    this.updateVideoProgress();
    this.progressFrame = requestAnimationFrame(() => this.runProgressFrame());
  }

  updateVideoProgress() {
    const duration = this.progressVideo?.duration;
    const progress = Number.isFinite(duration) && duration > 0 ? this.progressVideo.currentTime / duration : 0;
    this.setProgress(progress);
  }

  setProgress(progress) { if (this.progressFill) this.progressFill.style.transform = `scaleX(${Math.min(1, Math.max(0, progress))})`; }

  stopProgressFrame() { cancelAnimationFrame(this.progressFrame); this.progressFrame = 0; }

  stopVideoProgress(reset = false) {
    this.stopProgressFrame();
    this.progressAbortController?.abort();
    if (reset) this.setProgress(0);
    this.progressAbortController = null;
    this.progressVideo = null;
    this.progressFill = null;
  }

  closeLightboxMedia() {
    this.stopVideoProgress(true);
    if (this.lightboxMountedIndex == null) return;
    const slide = this.lightboxSlides[this.lightboxMountedIndex];
    const mediaContainer = slide?.querySelector('[data-ryoxzel-ugc-lightbox-media]');
    mediaContainer?.querySelector('deferred-media')?.pauseMedia?.();
    const poster = slide?.querySelector('[data-ryoxzel-ugc-lightbox-poster]');
    if (mediaContainer && poster) mediaContainer.replaceChildren(poster.content.cloneNode(true));
    this.lightboxMountedIndex = null;
    if (!this.lightboxDialog?.open) { this.lightboxMuted = true; this.updateAllSoundButtons(); }
  }

  destroy() {
    this.closeLightboxMedia();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.lightboxFrame);
    this.stopVideoProgress(true);
    this.abortController.abort();
  }
}

function initShelf(section) {
  if (!(section instanceof HTMLElement) || shelfInstances.has(section)) return;
  shelfInstances.set(section, new RyoxzelUgcShelf(section));
}

function destroyShelf(section) {
  const instance = shelfInstances.get(section);
  instance?.destroy();
  shelfInstances.delete(section);
}

function findShelves(root) {
  if (!(root instanceof HTMLElement || root instanceof Document)) return [];
  const shelves = Array.from(root.querySelectorAll('[data-ryoxzel-ugc-shelf]'));
  if (root instanceof HTMLElement && root.matches('[data-ryoxzel-ugc-shelf]')) shelves.unshift(root);
  return shelves;
}

function initDocumentShelves() {
  findShelves(document).forEach(initShelf);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDocumentShelves, { once: true });
} else {
  initDocumentShelves();
}

document.addEventListener('shopify:section:load', (event) => {
  findShelves(event.target).forEach(initShelf);
});

document.addEventListener('shopify:section:unload', (event) => {
  findShelves(event.target).forEach(destroyShelf);
});
